import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Printer } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetchBlob } from "@/lib/api";

import { downloadRmaDocument, fetchQualityRmaDetail, qualityRmaQueryKey } from "./rmaApi";

export function QualityRmaDocumentPage() {
  const params = useParams<{ id: string }>();
  const rmaId = Number(params.id);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [kind, setKind] = useState<"customer" | "internal">("customer");
  const detailQuery = useQuery({
    queryKey: qualityRmaQueryKey(rmaId),
    queryFn: () => fetchQualityRmaDetail(rmaId),
    enabled: Number.isFinite(rmaId) && rmaId > 0,
  });

  useEffect(() => {
    if (!Number.isFinite(rmaId) || rmaId < 1) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    void apiFetchBlob(
      `/api/quality/rmas/${rmaId}/document${kind === "internal" ? "?kind=internal" : ""}`,
    ).then(({ blob }) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setDocumentUrl(objectUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rmaId, kind]);

  if (!Number.isFinite(rmaId) || rmaId < 1) {
    return <p className="text-sm text-red-700">Invalid RMA id.</p>;
  }
  if (detailQuery.isLoading) {
    return <p className="text-sm text-slate-600">Loading RMA document…</p>;
  }
  if (detailQuery.isError || detailQuery.data == null) {
    return <p className="text-sm text-red-700">{(detailQuery.error as Error)?.message ?? "RMA was not found."}</p>;
  }

  const rma = detailQuery.data.rma;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <PageTitleWithHelp
          hoverHint="Download saves this RMA form as a PDF."
          breadcrumbLabel={`${rma.rmaNumber} document`}
        >
          {rma.rmaNumber} document
        </PageTitleWithHelp>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={kind === "customer" ? "primary" : "secondary"}
            onClick={() => setKind("customer")}
          >
            Customer form
          </Button>
          <Button
            variant={kind === "internal" ? "primary" : "secondary"}
            onClick={() => setKind("internal")}
          >
            Internal log
          </Button>
          <Button variant="secondary" onClick={() => void downloadRmaDocument(rma.id, rma.rmaNumber, kind)}>
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>
      </header>
      <iframe
        title={`${rma.rmaNumber} document`}
        className="min-h-[80vh] w-full rounded border border-slate-200 bg-white"
        src={documentUrl ?? undefined}
      />
    </div>
  );
}
